/**
 * Glossary — searchable plain-language financial terms.
 * Mirrors the mobile app's app/glossary.tsx.
 */
import { useMemo, useState } from "react";
import { WalletShell, useWalletActive, NAVY, GOLD } from "@/components/wallet/WalletShell";
import { Search, BookOpen } from "lucide-react";
import { emitSignal } from "@/lib/wallet-signal";

const TERMS = [
  { term: "APR",                 def: "The total yearly cost of a loan, including interest AND fees. Always higher than the interest rate alone." },
  { term: "Cooling-off period",  def: "A short window (typically 5–31 days) after signing during which you can cancel without penalty." },
  { term: "Compound interest",   def: "Interest that also earns interest. Great when you're saving; expensive when you're borrowing." },
  { term: "Debt review",         def: "A legal process where a court-appointed counsellor renegotiates your debts. You cannot take new credit while under review." },
  { term: "Diversification",     def: "Not putting all your money into one thing. Spreads risk across different investments." },
  { term: "EAC",                 def: "Effective Annual Cost — the total yearly cost of a retirement product. Small differences add up massively over decades." },
  { term: "Exclusions",          def: "Situations your insurance policy WILL NOT pay out for. Always read these before you sign." },
  { term: "FICA",                def: "Financial Intelligence Centre Act — the law that requires banks to verify your identity." },
  { term: "FSCA",                def: "Financial Sector Conduct Authority — South Africa's financial regulator. They keep a public list of who is licensed." },
  { term: "Interest rate",       def: "The percentage a lender charges you (or pays you) each year for the use of money." },
  { term: "Inflation",           def: "The rate at which prices rise over time. R100 today buys less next year." },
  { term: "MFA",                 def: "Multi-Factor Authentication — a second proof of identity beyond your password (e.g. SMS code, app prompt). Always turn it on." },
  { term: "POPIA",               def: "Protection of Personal Information Act — the South African law giving you rights over your personal data." },
  { term: "Prime rate",          def: "The base interest rate banks charge their best customers. Everyone else pays prime plus a margin." },
  { term: "Repo rate",           def: "The rate the South African Reserve Bank charges commercial banks. When it goes up, your bond and loan repayments usually rise too." },
  { term: "TCF",                 def: "Treating Customers Fairly — the FSCA outcomes framework that says banks must communicate clearly and put customer outcomes first." },
  { term: "Term",                def: "How long a product runs — the term of a loan, the term of an insurance policy, the term of an investment." },
  { term: "Underwriting",        def: "The bank or insurer checking whether they will accept you as a customer and on what terms." },
];

export default function WalletGlossary() {
  const [q, setQ] = useState("");
  const [openTerm, setOpenTerm] = useState(null);
  const active = useWalletActive();
  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (query) emitSignal("glossary.searched.v1", { query, results: TERMS.filter(t => t.term.toLowerCase().includes(query)).length });
    if (!query) return TERMS;
    return TERMS.filter(t => t.term.toLowerCase().includes(query) || t.def.toLowerCase().includes(query));
  }, [q]);

  const viewTerm = (term) => {
    setOpenTerm(term);
    emitSignal("glossary.term.viewed.v1", { term, in_review_context: window.location.pathname.startsWith("/j/") });
  };

  return (
    <WalletShell activeTab={active}>
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4" data-testid="wallet-glossary">
        <header>
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Glossary</div>
          <h1 className="font-display text-2xl font-semibold" style={{ color: NAVY }}>Plain-language financial terms</h1>
        </header>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search e.g. TCF, cooling-off, APR"
            className="w-full pl-9 pr-3 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-slate-400"
            data-testid="glossary-search"
          />
        </div>

        <div className="rounded-2xl bg-white border border-slate-100 shadow-sm divide-y divide-slate-100">
          {filtered.map(t => (
            <button key={t.term} onClick={() => viewTerm(t.term)} data-testid={`glossary-term-${t.term}`} className="w-full text-left p-4 hover:bg-slate-50 transition">
              <div className="flex items-start gap-3">
                <BookOpen className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: GOLD }} />
                <div>
                  <div className="text-sm font-semibold" style={{ color: NAVY }}>{t.term}</div>
                  <div className="text-[13px] text-slate-700 mt-1 leading-relaxed">{t.def}</div>
                </div>
              </div>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-400">No matches. Try a different search.</div>
          )}
        </div>
      </div>
    </WalletShell>
  );
}
